/**
 * Compatibility progress for legacy agent retrains, lifted to app level so it
 * survives navigation and is visible from any page.
 *
 * Source of truth is `knowledge.reingest_runs` (written by the document-loader
 * job, one row per run, discriminated by `kind`). This context polls the latest
 * run per kind for the selected domain — faster while one is running, plus a
 * post-click watch window to catch the row that appears a few seconds after
 * triggering (Cloud Run cold start). It renders nothing itself: progress is
 * shown on the Knowledge screens only (IX-4724), everywhere else the spinning
 * Rose mark in the sidebar is the whole signal. Durable knowledge operations
 * report through KnowledgeOperationProgressContext instead.
 */

import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { toast } from 'sonner';
import { useKnowledgeOperationProgress } from '@/contexts/KnowledgeOperationProgressContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import type { KnowledgeOperation, KnowledgeProgressScope } from '@/domain/knowledgeOperations';
import {
    fetchLatestReingestRun,
    type KnowledgeReingestKind,
    type KnowledgeReingestRun,
    reingestKnowledgeDomain,
} from '@/services/knowledgeDocuments';

const KINDS: KnowledgeReingestKind[] = ['documents', 'faqs'];
const KIND_LABEL: Record<KnowledgeReingestKind, string> = {
    documents: 'knowledge',
    faqs: 'FAQs',
};
const KIND_SCOPE: Record<KnowledgeReingestKind, KnowledgeProgressScope> = {
    documents: 'managed',
    faqs: 'knowledge_faq',
};

const WATCH_TIMEOUT_MS = 180_000;
const POLL_INTERVAL_MS = 2_000;

type PerKind<T> = Record<KnowledgeReingestKind, T>;

interface RetrainKindState {
    run: KnowledgeReingestRun | null;
    isRunning: boolean;
    isBusy: boolean;
    triggerRetrain: () => Promise<void>;
}

/** A legacy retrain in flight, for the app-wide "Rose is learning" signal. */
export interface LegacyRetrainLearning {
    title: string;
    label: string;
    processed: number | null;
    total: number | null;
}

interface RetrainProgressValue {
    forKind: (kind: KnowledgeReingestKind) => RetrainKindState;
    /**
     * The legacy retrain this provider would have bannered, or null. Feeds
     * useKnowledgeLearning so the sidebar mark and the Knowledge hub see a
     * documents/FAQ re-ingest the same way they see a knowledge operation.
     */
    legacyLearning: LegacyRetrainLearning | null;
    /**
     * A knowledge ingest is running somewhere for this domain — documents, FAQs
     * or the website. Every ingest feeds the same tenant knowledge base, so the
     * surfaces have to take turns.
     */
    anyTrainingActive: boolean;
}

const RetrainProgressContext = createContext<RetrainProgressValue | null>(null);

const emptyRuns: PerKind<KnowledgeReingestRun | null> = { documents: null, faqs: null };
const zeroWatch: PerKind<number> = { documents: 0, faqs: 0 };

function reingestRunFromOperation(operation: KnowledgeOperation): KnowledgeReingestRun {
    const status =
        operation.status === 'queued' ||
        operation.status === 'running' ||
        operation.status === 'cancelling'
            ? 'running'
            : operation.status === 'succeeded' || operation.status === 'partial_failed'
              ? 'succeeded'
              : 'failed';
    // Show the real re-ingest item counts (set once the loader phase starts),
    // not progress_percent — otherwise the UI prints a percentage as an item
    // count ("0/100 FAQs"). total stays 0 until the loader reports, so both the
    // FAQ page and the banner fall back to the indeterminate "queued/starting"
    // text + pulsing bar instead of a bogus "/100".
    return {
        id: operation.id,
        site_domain: operation.site_domain,
        status,
        processed: operation.loader_processed ?? 0,
        total: operation.loader_total ?? 0,
        error:
            operation.status === 'cancelled'
                ? `The knowledge refresh for ${operation.site_domain} was cancelled.`
                : operation.status === 'failed'
                  ? `The knowledge refresh for ${operation.site_domain} could not finish. Try it again.`
                  : null,
        started_at: operation.created_at,
        finished_at: operation.completed_at,
    };
}

export function RetrainProgressProvider({ children }: { children: ReactNode }) {
    const { selectedWorkspaceId } = useWorkspace();
    const {
        isRestoring: isOperationProgressRestoring,
        progressOwner,
        trackOperation,
        claimLegacyProgress,
        operationForScope,
        isOperationActive,
    } = useKnowledgeOperationProgress();

    const [runs, setRuns] = useState<PerKind<KnowledgeReingestRun | null>>(emptyRuns);
    const [watchSince, setWatchSince] = useState<PerKind<number>>(zeroWatch);
    const [triggering, setTriggering] = useState<PerKind<boolean>>({
        documents: false,
        faqs: false,
    });
    const selectedWorkspaceRef = useRef(selectedWorkspaceId);
    selectedWorkspaceRef.current = selectedWorkspaceId;
    const sawRunning = useRef<PerKind<boolean>>({ documents: false, faqs: false });

    const usesLegacyProgress = useCallback(
        (kind: KnowledgeReingestKind) =>
            !isOperationProgressRestoring && progressOwner(KIND_SCOPE[kind]) === 'legacy',
        [isOperationProgressRestoring, progressOwner]
    );

    const refreshRun = useCallback(
        async (kind: KnowledgeReingestKind) => {
            if (!selectedWorkspaceId || !usesLegacyProgress(kind)) {
                setRuns((prev) => ({ ...prev, [kind]: null }));
                return;
            }
            const run = await fetchLatestReingestRun(selectedWorkspaceId, kind);
            if (selectedWorkspaceRef.current !== selectedWorkspaceId) return;
            setRuns((prev) => ({ ...prev, [kind]: run }));
        },
        [selectedWorkspaceId, usesLegacyProgress]
    );

    // Reset only on workspace changes: operation polling recreates refreshRun.
    const resetWorkspaceRef = useRef<string | null>(null);
    useEffect(() => {
        if (resetWorkspaceRef.current !== selectedWorkspaceId) {
            resetWorkspaceRef.current = selectedWorkspaceId;
            setRuns(emptyRuns);
            setTriggering({ documents: false, faqs: false });
            setWatchSince(zeroWatch);
            sawRunning.current = { documents: false, faqs: false };
        }
        for (const kind of KINDS) void refreshRun(kind);
    }, [selectedWorkspaceId, refreshRun]);

    const isActive = (kind: KnowledgeReingestKind) =>
        usesLegacyProgress(kind) && (runs[kind]?.status === 'running' || watchSince[kind] > 0);
    const anyActive = KINDS.some(isActive);

    // Poll active kinds. Read latest state via refs so the interval isn't torn
    // down and rebuilt on every state change.
    const runsRef = useRef(runs);
    runsRef.current = runs;
    const watchRef = useRef(watchSince);
    watchRef.current = watchSince;
    useEffect(() => {
        if (!anyActive) return;
        const id = setInterval(() => {
            for (const kind of KINDS) {
                if (runsRef.current[kind]?.status === 'running' || watchRef.current[kind] > 0) {
                    void refreshRun(kind);
                }
            }
        }, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [anyActive, refreshRun]);

    // End each kind's watch once its triggered run reaches a terminal state
    // (after we've seen it running) or after a safety timeout.
    useEffect(() => {
        setWatchSince((prev) => {
            let changed = false;
            const next = { ...prev };
            for (const kind of KINDS) {
                if (runs[kind]?.status === 'running') sawRunning.current[kind] = true;
                if (prev[kind] > 0) {
                    const run = runs[kind];
                    const finished =
                        sawRunning.current[kind] && run != null && run.status !== 'running';
                    const timedOut = Date.now() - prev[kind] > WATCH_TIMEOUT_MS;
                    if (finished || timedOut) {
                        next[kind] = 0;
                        changed = true;
                    }
                }
            }
            return changed ? next : prev;
        });
    }, [runs]);

    const triggerRetrain = useCallback(
        async (kind: KnowledgeReingestKind) => {
            if (!selectedWorkspaceId) return;
            setTriggering((t) => ({ ...t, [kind]: true }));
            sawRunning.current[kind] = false;
            try {
                const response = await reingestKnowledgeDomain(selectedWorkspaceId, kind);
                if (selectedWorkspaceRef.current !== selectedWorkspaceId) return;
                if (response.progress_owner === 'knowledge_operation' && response.operation_id) {
                    const scope = KIND_SCOPE[kind];
                    setRuns((current) => ({ ...current, [kind]: null }));
                    setWatchSince((current) => ({ ...current, [kind]: 0 }));
                    await trackOperation(
                        {
                            operation_id: response.operation_id,
                            status: 'queued',
                            status_url:
                                response.status_url ??
                                `/admin/knowledge/operations/${response.operation_id}`,
                            deduplicated: false,
                        },
                        {
                            workspaceId: selectedWorkspaceId,
                            operationType: 'document_loader_retrain',
                            sourceScope: scope === 'knowledge_faq' ? 'knowledge_faq' : 'managed',
                        }
                    );
                    return;
                }

                claimLegacyProgress(KIND_SCOPE[kind]);
                toast.success('Agent retrain queued — re-ingesting knowledge into the agent');
                setWatchSince((w) => ({ ...w, [kind]: Date.now() }));
                await refreshRun(kind);
            } catch (err) {
                if (selectedWorkspaceRef.current === selectedWorkspaceId)
                    toast.error(err instanceof Error ? err.message : 'Failed to queue retrain');
            } finally {
                if (selectedWorkspaceRef.current === selectedWorkspaceId)
                    setTriggering((t) => ({ ...t, [kind]: false }));
            }
        },
        [selectedWorkspaceId, trackOperation, claimLegacyProgress, refreshRun]
    );

    const forKind = useCallback(
        (kind: KnowledgeReingestKind): RetrainKindState => {
            const scope = KIND_SCOPE[kind];
            const operation =
                progressOwner(scope) === 'knowledge_operation' ? operationForScope(scope) : null;
            const operationRun = operation ? reingestRunFromOperation(operation) : null;
            const operationActive = isOperationActive(scope);
            return {
                run: operationRun ?? runs[kind],
                isRunning: operationActive || runs[kind]?.status === 'running',
                isBusy:
                    triggering[kind] ||
                    operationActive ||
                    (usesLegacyProgress(kind) &&
                        (runs[kind]?.status === 'running' || watchSince[kind] > 0)),
                triggerRetrain: () => triggerRetrain(kind),
            };
        },
        [
            progressOwner,
            operationForScope,
            isOperationActive,
            runs,
            triggering,
            watchSince,
            usesLegacyProgress,
            triggerRetrain,
        ]
    );

    const websiteOperationActive = isOperationActive('website');

    const anyTrainingActive = websiteOperationActive || KINDS.some((kind) => forKind(kind).isBusy);

    // Only report a real legacy run. `isBusy` is also true for the in-flight
    // trigger request, and the retrain API usually answers with a knowledge
    // operation — reporting that window would flash a second signal before the
    // operation takes over. An active website operation owns its own loader
    // phase, so a stale legacy row must not compete with it either.
    const legacyKind = websiteOperationActive
        ? undefined
        : KINDS.find(
              (kind) =>
                  usesLegacyProgress(kind) &&
                  (runs[kind]?.status === 'running' || watchSince[kind] > 0)
          );
    const legacyRun = legacyKind ? runs[legacyKind] : null;
    const legacyRunning = legacyRun?.status === 'running';
    const legacyLearning: LegacyRetrainLearning | null = legacyKind
        ? {
              title: 'Retraining agent',
              label: legacyRunning ? `Re-ingesting ${KIND_LABEL[legacyKind]}…` : 'Starting…',
              processed: legacyRunning ? (legacyRun?.processed ?? null) : null,
              total: legacyRunning ? (legacyRun?.total ?? null) : null,
          }
        : null;

    return (
        <RetrainProgressContext.Provider value={{ forKind, anyTrainingActive, legacyLearning }}>
            {children}
        </RetrainProgressContext.Provider>
    );
}

/**
 * True while any knowledge ingest is in flight for the selected domain. The
 * document, FAQ and website surfaces all gate their retrain buttons on it: two
 * concurrent re-ingests race on the same knowledge base.
 */
export function useAnyTrainingActive(): boolean {
    const ctx = useContext(RetrainProgressContext);
    if (!ctx) {
        throw new Error('useAnyTrainingActive must be used within a RetrainProgressProvider');
    }
    return ctx.anyTrainingActive;
}

/**
 * The legacy retrain in flight, if any. Non-throwing: the app-wide learning
 * signal reads it from surfaces that may render outside this provider.
 */
export function useLegacyRetrainLearning(): LegacyRetrainLearning | null {
    return useContext(RetrainProgressContext)?.legacyLearning ?? null;
}

export function useRetrainProgress(kind: KnowledgeReingestKind): RetrainKindState {
    const ctx = useContext(RetrainProgressContext);
    if (!ctx) {
        throw new Error('useRetrainProgress must be used within a RetrainProgressProvider');
    }
    return ctx.forKind(kind);
}
